import React, { useEffect, useMemo, useState } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { ExternalLink, ReceiptText, FileText } from 'lucide-react';

function formatMoney(currency, value) {
  const n = Number(value || 0);
  return `${currency || 'UGX'} ${Number.isNaN(n) ? value : n.toLocaleString()}`;
}

function formatDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString();
}

function badgeClass(status) {
  const s = String(status || '').toLowerCase();
  if (s === 'paid' || s === 'successful' || s === 'active' || s === 'accepted') return 'bg-emerald-100 text-emerald-800';
  if (s === 'sent' || s === 'draft' || s === 'pending' || s === 'unpaid') return 'bg-amber-100 text-amber-800';
  if (s === 'failed' || s === 'cancelled' || s === 'rejected') return 'bg-red-100 text-red-800';
  return 'bg-slate-100 text-slate-700';
}

export default function CommerceDocsAdminPage({ mode }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const title = mode === 'invoice' ? 'Invoices' : 'Receipts';
  const endpoint = mode === 'invoice' ? '/admin/invoices' : '/admin/receipts';

  const params = useMemo(() => ({
    user_id: userId || undefined,
    status: status || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  }), [userId, status, dateFrom, dateTo]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(endpoint, { params });
      setItems(res.data?.[mode === 'invoice' ? 'invoices' : 'receipts'] || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to load ${title.toLowerCase()}`);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endpoint, userId, status, dateFrom, dateTo]);

  const openPublic = (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  if (loading) {
    return <div className="flex items-center justify-center h-40"><div className="w-6 h-6 border-2 border-emerald-700 border-t-transparent rounded-full animate-spin" /></div>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl md:text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h1>
        <p className="text-slate-500 text-sm mt-0.5">Filter and review {title.toLowerCase()} across all users.</p>
      </div>

      <Card className="bg-white border border-slate-200 shadow-sm rounded-2xl">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Outfit, sans-serif' }}>Filters</CardTitle>
          <div className="flex flex-wrap gap-3 mt-3">
            <Input className="w-56" placeholder="User ID" value={userId} onChange={(e) => setUserId(e.target.value)} />
            <Select value={status || 'all'} onValueChange={(value) => setStatus(value === 'all' ? '' : value)}>
              <SelectTrigger className="w-44"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {mode === 'invoice' ? (
                  <>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="unpaid">Unpaid</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </>
                ) : (
                  <>
                    <SelectItem value="mobile_money">Mobile money</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="bank_transfer">Bank transfer</SelectItem>
                  </>
                )}
              </SelectContent>
            </Select>
            <Input className="w-48" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
            <Input className="w-48" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
            <Button type="button" variant="outline" onClick={load}>Refresh</Button>
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{mode === 'invoice' ? 'Invoice #' : 'Receipt #'}</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Customer</TableHead>
                {mode === 'invoice' ? <TableHead>Title</TableHead> : <TableHead>Invoice</TableHead>}
                <TableHead>Total</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <TableRow key={item.id || item._id}>
                  <TableCell className="font-mono text-xs break-all">{mode === 'invoice' ? (item.invoice_number || '-') : (item.receipt_number || '-')}</TableCell>
                  <TableCell>
                    <div className="text-sm font-medium text-slate-900">{item.owner?.name || item.user_id || '-'}</div>
                    <div className="text-xs text-slate-500 break-all">{item.owner?.email || '-'}</div>
                  </TableCell>
                  <TableCell>
                    <div className="font-medium text-slate-900">{item.customer?.name || item.customer_name || '-'}</div>
                    <div className="text-xs text-slate-500">{item.customer?.phone || '-'}</div>
                  </TableCell>
                  <TableCell>{mode === 'invoice' ? (item.title || '-') : (item.invoice?.invoice_number || item.invoice_id || '-')}</TableCell>
                  <TableCell>{formatMoney(item.currency, item.total || item.amount || 0)}</TableCell>
                  <TableCell><Badge className={badgeClass(item.status || item.payment_method)}>{String(item.status || item.payment_method || '-').replace(/_/g, ' ')}</Badge></TableCell>
                  <TableCell>{formatDate(item.created_at || item.issued_at)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-2">
                      {item.public_url && (
                        <Button type="button" variant="outline" size="sm" onClick={() => openPublic(item.public_url)}>
                          <ExternalLink size={14} className="mr-1" /> Open
                        </Button>
                      )}
                      {mode === 'invoice' && item.receipt?.public_url && (
                        <Button type="button" variant="outline" size="sm" onClick={() => openPublic(item.receipt.public_url)}>
                          <ReceiptText size={14} className="mr-1" /> Receipt
                        </Button>
                      )}
                      {mode === 'receipt' && item.invoice?.public_url && (
                        <Button type="button" variant="outline" size="sm" onClick={() => openPublic(item.invoice.public_url)}>
                          <FileText size={14} className="mr-1" /> Invoice
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
